class SwatchBuilderFixedBar extends window.BAO.CustomElement() {
  constructor () {
    super()
    this.onProductSelected = this.onProductSelected.bind(this)
    this.onRemoveSwatch = this.onRemoveSwatch.bind(this)
    this.onQuickAddClick = this.onQuickAddClick.bind(this)
    this.selectedSwatches = new Map()
    this.customerTier = this.dataset.customerTier || 'guest'
  }

  async connectedCallback () {
    super.connectedCallback()

    this.selectedSwatches = this.loadFromSessionStorage()

    if (this.selectedSwatches.size > 0) {
      await this.restoreSelectedSwatches()
      this.calculateAndUpdatePrice()
      this.showAllSelectedIcons()

      await new Promise((resolve) => setTimeout(resolve, 1000))

      this.setVisibility(true)
    } else {
      this.setVisibility(false)
    }
  }

  saveToSessionStorage () {
    const mapAsArray = Array.from(this.selectedSwatches.entries())

    sessionStorage.setItem(
      'BAO-swatch-builder-selections',
      JSON.stringify(mapAsArray),
    )
  }

  loadFromSessionStorage () {
    const stored = sessionStorage.getItem('BAO-swatch-builder-selections')

    if (stored) {
      const mapAsArray = JSON.parse(stored)
      return new Map(mapAsArray)
    }
    return new Map()
  }

  calculateAndUpdatePrice () {
    let totalPrice = 0

    for (const swatchData of this.selectedSwatches.values()) {
      if (
        swatchData.productData &&
        typeof swatchData.productData.price === 'number'
      ) {
        totalPrice += swatchData.productData.price
      }
    }

    const formattedPrice = this.formatPrice(totalPrice)
    const subtotalEl = this.els.submitBtnSubtotal?.element

    if (subtotalEl) {
      subtotalEl.textContent = formattedPrice
    }
  }

  formatPrice (price) {
    return window.BAO.Utils.formatMoney(price)
  }

  setVisibility (isVisible) {
    const value = isVisible ? 'false' : 'true'
    this.setAttribute('aria-hidden', value)

    if (this.els.submitBtnSubtotal?.element) {
      this.els.submitBtnSubtotal.element.setAttribute('aria-hidden', value)
    }
    if (this.els.submitBtnDivider?.element) {
      this.els.submitBtnDivider.element.setAttribute('aria-hidden', value)
    }
  }

  setupListeners () {
    super.setupListeners()

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.SWATCH_BUILDER.PRODUCT_SELECTED,
      this.onProductSelected,
    )

    this.addEventListener('click', this.onRemoveSwatch)
    this.addEventListener('click', this.onSubmitClick)

    document.addEventListener('click', (event) => {
      const quickAddSelector = this.externalEls.quickAdd?.selector
      const quickAddSwatchMultiGridSelector =
        this.externalEls.quickAddSwatchMultiGrid?.selector

      const quickAddButton = quickAddSelector
        ? event.target.closest(quickAddSelector)
        : null

      const quickAddSwatchMultiGridButton = quickAddSwatchMultiGridSelector
        ? event.target.closest(quickAddSwatchMultiGridSelector)
        : null

      const button = quickAddButton || quickAddSwatchMultiGridButton

      if (button) {
        this.onQuickAddClick(event)
      }
    })

    this.collectionFilterObserver()
  }

  collectionFilterObserver () {
    const collectionFilters = this.closest('collection-filters')

    if (!collectionFilters) return

    this.collectionFiltersObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-busy'
        ) {
          const isLoading =
            collectionFilters.getAttribute('aria-busy') === 'true'

          if (!isLoading && this.selectedSwatches.size > 0) {
            setTimeout(() => {
              this.showAllSelectedIcons()
            }, 100)
          }
        }
      }
    })

    this.collectionFiltersObserver.observe(collectionFilters, {
      attributes: true,
      attributeFilter: ['aria-busy'],
    })
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    if (this.collectionFiltersObserver) {
      this.collectionFiltersObserver.disconnect()
    }
  }

  setSwatchItemActive (productHandle) {
    const swatchItem = this.externalEls.swatchItem.elements.find((el) => {
      const handleEl = el.querySelector('[data-product-handle]')
      return (
        handleEl &&
        handleEl.getAttribute('data-product-handle') === productHandle
      )
    })

    if (swatchItem) {
      const quickAddSelector = this.externalEls.quickAdd?.selector
      const quickAddSwatchMultiGridSelector =
        this.externalEls.quickAddSwatchMultiGrid?.selector

      const quickAddButtons = quickAddSelector
        ? swatchItem.querySelectorAll(quickAddSelector)
        : []

      const quickAddSwatchMultiGridButtons = quickAddSwatchMultiGridSelector
        ? swatchItem.querySelectorAll(quickAddSwatchMultiGridSelector)
        : []

      if (quickAddButtons.length > 0) {
        quickAddButtons.forEach((button) => {
          button.classList.add('prd-Card_QuickAddButton-active')
        })
      }

      if (quickAddSwatchMultiGridButtons.length > 0) {
        quickAddSwatchMultiGridButtons.forEach((button) => {
          button.classList.add('sec-MultiColumnGrid_QuickAddButton-active')
        })
      }
    }
  }

  hideSwatchItemActive (productHandle) {
    const swatchItem = this.externalEls.swatchItem.elements.find((el) => {
      const handleEl = el.querySelector('[data-product-handle]')
      return (
        handleEl &&
        handleEl.getAttribute('data-product-handle') === productHandle
      )
    })

    if (swatchItem) {
      const quickAddSelector = this.externalEls.quickAdd?.selector
      const quickAddSwatchMultiGridSelector =
        this.externalEls.quickAddSwatchMultiGrid?.selector

      const quickAddButtons = quickAddSelector
        ? swatchItem.querySelectorAll(quickAddSelector)
        : []

      const quickAddSwatchMultiGridButtons = quickAddSwatchMultiGridSelector
        ? swatchItem.querySelectorAll(quickAddSwatchMultiGridSelector)
        : []

      if (quickAddButtons.length > 0) {
        quickAddButtons.forEach((button) => {
          button.classList.remove('prd-Card_QuickAddButton-active')
        })
      }

      if (quickAddSwatchMultiGridButtons.length > 0) {
        quickAddSwatchMultiGridButtons.forEach((button) => {
          button.classList.remove('sec-MultiColumnGrid_QuickAddButton-active')
        })
      }
    }
  }

  showAllSelectedIcons () {
    this.selectedSwatches.forEach((_, productHandle) => {
      this.setSwatchItemActive(productHandle)
    })
  }

  onProductSelected (event) {
    const { productHandle } = event.detail

    if (productHandle) {
      this.addSwatch(productHandle)
    }
  }

  onQuickAddClick (event) {
    const quickAddSelector = this.externalEls.quickAdd?.selector
    const quickAddSwatchMultiGridSelector =
      this.externalEls.quickAddSwatchMultiGrid?.selector

    const quickAddButton = quickAddSelector
      ? event.target.closest(quickAddSelector)
      : null
    const quickAddSwatchMultiGridButton = quickAddSwatchMultiGridSelector
      ? event.target.closest(quickAddSwatchMultiGridSelector)
      : null
    const button = quickAddButton || quickAddSwatchMultiGridButton
    const productHandle = button?.getAttribute('data-product-handle')

    if (productHandle) {
      if (this.selectedSwatches.has(productHandle)) {
        this.removeSwatch(productHandle)
      } else {
        this.addSwatch(productHandle)
      }
    } else {
      console.error('Could not find product handle for quick add')
    }
  }

  onRemoveSwatch (event) {
    const removeButton = event.target.closest(this.els.removeSwatch.selector)

    if (removeButton) {
      const swatchItem = removeButton.closest(this.els.swatchItem.selector)
      const productHandle = swatchItem.dataset.productHandle

      if (productHandle) {
        this.removeSwatch(productHandle)
      }
    }
  }

  onSubmitClick (event) {
    const submitBtn = event.target.closest(this.els.submitBtn.selector)

    if (submitBtn) {
      this.addAllSwatchesToCart()
    }
  }

  async addSwatch (productHandle) {
    if (this.selectedSwatches.has(productHandle)) {
      alert(window.theme.strings.swatchAlreadyInSelection)
      return
    }

    try {
      const productData = await this.fetchProductData(productHandle)

      if (productData) {
        const emptySlot = await this.findEmptySlot()

        if (emptySlot) {
          this.selectedSwatches.set(productHandle, {
            productHandle,
            productData,
            slotIndex: emptySlot.getAttribute('data-swatch-index'),
          })

          this.setAttribute('aria-hidden', 'false')

          if (this.els.submitBtnSubtotal?.element) {
            this.els.submitBtnSubtotal.element.setAttribute(
              'aria-hidden',
              'false',
            )
          }

          if (this.els.submitBtnDivider?.element) {
            this.els.submitBtnDivider.element.setAttribute(
              'aria-hidden',
              'false',
            )
          }

          this.updateSlotWithProduct(emptySlot, productData, productHandle)

          this.calculateAndUpdatePrice()

          this.setSwatchItemActive(productHandle)

          this.saveToSessionStorage()
        }
      }
    } catch (error) {
      console.error('Error adding swatch:', error)
    }
  }

  async removeSwatch (productHandle) {
    const swatchData = this.selectedSwatches.get(productHandle)

    if (swatchData) {
      this.selectedSwatches.delete(productHandle)
      this.saveToSessionStorage()

      if (this.selectedSwatches.size === 0) {
        this.setVisibility(false)
        this.els.swatchItem.elements.forEach((slot) => this.resetSlot(slot))
        this.calculateAndUpdatePrice()
      } else {
        await this.reorganizeSwatches()
        this.calculateAndUpdatePrice()
      }

      this.hideSwatchItemActive(productHandle)
    }
  }

  async reorganizeSwatches () {
    const remainingSwatches = Array.from(this.selectedSwatches.values())
    const slotCountBeforeReset = this.els.swatchItem.elements.length

    this.els.swatchItem.elements.forEach((slot) => this.resetSlot(slot))

    const targetSlotCount = Math.max(slotCountBeforeReset, 11)
    const currentSlotCount = this.els.swatchItem.elements.length

    if (currentSlotCount < targetSlotCount) {
      for (let i = currentSlotCount + 1; i <= targetSlotCount; i++) {
        await this.createSlotForIndex(i)
      }
    }

    this.els.swatchItem.elements = this.querySelectorAll(
      this.els.swatchItem.selector,
    )

    remainingSwatches.forEach((swatchData, i) => {
      const newSlotIndex = i + 1
      const slot = this.querySelector(`[data-swatch-index="${newSlotIndex}"]`)

      if (slot) {
        swatchData.slotIndex = newSlotIndex
        this.selectedSwatches.set(swatchData.productHandle, swatchData)
        this.updateSlotWithProduct(
          slot,
          swatchData.productData,
          swatchData.productHandle,
        )
      }
    })

    this.saveToSessionStorage()
  }

  orderSwatches () {
    const swatchList = this.els.swatchList?.element
    if (!swatchList) return

    const slots = Array.from(
      swatchList.querySelectorAll(this.els.swatchItem.selector),
    ).sort((a, b) => {
      const indexA = parseInt(a.getAttribute('data-swatch-index')) || 0
      const indexB = parseInt(b.getAttribute('data-swatch-index')) || 0
      return indexA - indexB
    })

    slots.forEach((slot) => {
      swatchList.appendChild(slot)
    })

    this.els.swatchItem.elements = this.querySelectorAll(
      this.els.swatchItem.selector,
    )
  }

  removeExcessSlots (requiredSlots) {
    const allSlots = Array.from(this.els.swatchItem.elements)
    const minSlots = Math.max(requiredSlots + 1, 11)

    if (allSlots.length <= minSlots) return

    allSlots
      .slice(minSlots)
      .filter((slot) =>
        slot.classList.contains('clc-FixedBar_SwatchSelectionItem-empty'),
      )
      .forEach((slot) => slot.remove())
  }

  async findEmptySlot () {
    this.els.swatchItem.elements = this.querySelectorAll(
      this.els.swatchItem.selector,
    )

    const emptySlot = Array.from(this.els.swatchItem.elements).find((slot) =>
      slot.classList.contains('clc-FixedBar_SwatchSelectionItem-empty'),
    )

    if (emptySlot) return emptySlot

    return await this.createNewSlot()
  }

  async createNewSlot () {
    const slotIndex = this.selectedSwatches.size + 1

    return await this.createSlotForIndex(slotIndex)
  }

  async createSlotForIndex (slotIndex) {
    const swatchList = this.els.swatchList?.element

    if (!swatchList) return null

    try {
      const url = `${window.location.pathname}?slot_index=${slotIndex}`
      const sectionHtml = await window.BAO.sectionRenderer.fetchSingle(
        'swatch-builder-slot',
        url,
      )

      const parser = new DOMParser()
      const doc = parser.parseFromString(sectionHtml, 'text/html')

      const slotElement = doc.querySelector(
        '[data-swatch-builder-fixed-bar-el="swatchItem"]',
      )

      if (!slotElement) {
        console.error('Could not find slot element in rendered section')
        return null
      }

      const newSlot = slotElement.cloneNode(true)

      newSlot.setAttribute('data-swatch-index', slotIndex)

      const existingSlots = Array.from(
        swatchList.querySelectorAll(this.els.swatchItem.selector),
      ).sort((a, b) => {
        const indexA = parseInt(a.getAttribute('data-swatch-index')) || 0
        const indexB = parseInt(b.getAttribute('data-swatch-index')) || 0
        return indexA - indexB
      })

      let inserted = false
      for (const existingSlot of existingSlots) {
        const existingIndex =
          parseInt(existingSlot.getAttribute('data-swatch-index')) || 0
        if (slotIndex < existingIndex) {
          swatchList.insertBefore(newSlot, existingSlot)
          inserted = true
          break
        }
      }

      if (!inserted) {
        swatchList.appendChild(newSlot)
      }

      this.els.swatchItem.elements = this.querySelectorAll(
        this.els.swatchItem.selector,
      )

      return newSlot
    } catch (error) {
      console.error('Error creating slot via sectionRenderer:', error)
      return null
    }
  }

  updateSlotWithProduct (slot, productData, productHandle) {
    slot.classList.remove('clc-FixedBar_SwatchSelectionItem-empty')
    slot.dataset.productHandle = productHandle

    const placeholder = slot.querySelector(
      '.clc-FixedBar_SwatchSelectionItemPlaceholder',
    )
    const content = slot.querySelector(
      '.clc-FixedBar_SwatchSelectionItemContent',
    )

    if (placeholder && content) {
      placeholder.setAttribute('aria-hidden', 'true')
      content.setAttribute('aria-hidden', 'false')

      const img = content.querySelector(
        '.clc-FixedBar_SwatchSelectionItemImage',
      )
      if (img && productData.image) {
        const imageUrl =
          typeof productData.image === 'string'
            ? productData.image
            : productData.image.src || productData.image
        img.src = imageUrl
        img.alt = productData.title
      }

      const removeButton = content.querySelector(
        '[data-swatch-builder-fixed-bar-el="removeSwatch"]',
      )
      if (removeButton) {
        removeButton.setAttribute('aria-hidden', 'false')
      }
    }
  }

  resetSlot (slot) {
    slot.classList.add('clc-FixedBar_SwatchSelectionItem-empty')
    delete slot.dataset.productHandle

    const placeholder = slot.querySelector(
      '.clc-FixedBar_SwatchSelectionItemPlaceholder',
    )
    const content = slot.querySelector(
      '.clc-FixedBar_SwatchSelectionItemContent',
    )

    if (placeholder && content) {
      placeholder.setAttribute('aria-hidden', 'false')
      content.setAttribute('aria-hidden', 'true')

      const img = content.querySelector(
        '.clc-FixedBar_SwatchSelectionItemImage',
      )
      if (img) {
        img.src = ''
        img.alt = ''
      }
    }
  }

  async fetchProductData (productHandle) {
    try {
      const response = await fetch(`/products/${productHandle}.js`)
      const product = await response.json()

      const hasTierTag = this.customerTier !== 'guest'
      let selectedVariant = null
      let itemPrice = product.price

      if (product.variants && product.variants.length > 0) {
        if (hasTierTag) {
          selectedVariant = product.variants.find((variant) =>
            variant.title.includes('TRADE'),
          )
        } else {
          selectedVariant = product.variants.find(
            (variant) =>
              variant.title === 'Paid' ||
              variant.option1 === 'Paid' ||
              variant.options?.includes('Paid'),
          )
        }

        if (!selectedVariant) {
          selectedVariant =
            product.variants.find((v) => v.available) || product.variants[0]
        }

        if (selectedVariant) {
          itemPrice = selectedVariant.price
        } else if (product.price === 0 && product.variants.length > 0) {
          itemPrice = product.variants[0].price
        }
      }

      return {
        handle: product.handle,
        title: product.title,
        image: product.featured_image || product.images[0],
        price: itemPrice,
        variants: product.variants,
        selectedVariantId: selectedVariant?.id || product.variants?.[0]?.id,
      }
    } catch (error) {
      console.error('Error fetching product data:', error)

      return null
    }
  }

  async restoreSelectedSwatches () {
    const sortedSwatches = Array.from(this.selectedSwatches.entries()).sort(
      (a, b) => {
        const slotIndexA = parseInt(a[1].slotIndex) || 0
        const slotIndexB = parseInt(b[1].slotIndex) || 0
        return slotIndexA - slotIndexB
      },
    )

    let storageUpdated = false

    await Promise.all(
      sortedSwatches.map(async ([productHandle, swatchData]) => {
        if (swatchData.productData && swatchData.productData.image) return

        try {
          const productData = await this.fetchProductData(productHandle)

          if (productData) {
            swatchData.productData = productData
            this.selectedSwatches.set(productHandle, swatchData)
            storageUpdated = true
          }
        } catch (error) {
          console.error('Error fetching missing product data:', error)
        }
      }),
    )

    if (storageUpdated) {
      this.saveToSessionStorage()
    }

    for (const [productHandle, swatchData] of sortedSwatches) {
      if (!swatchData.productData) continue

      const slotIndex = parseInt(swatchData.slotIndex) || 0

      let slot = this.querySelector(`[data-swatch-index="${slotIndex}"]`)

      if (!slot) {
        slot = await this.createSlotForIndex(slotIndex)
      }

      if (!slot) continue

      this.updateSlotWithProduct(slot, swatchData.productData, productHandle)
    }

    this.orderSwatches()
  }

  async addAllSwatchesToCart () {
    if (this.selectedSwatches.size === 0) {
      console.warn('No swatches selected')
      return
    }

    const submitBtn = this.els.submitBtn?.element
    const submitBtnTextEl = submitBtn?.querySelector(
      '.clc-FixedBar_SubmitBtn-text',
    )
    const originalText = submitBtnTextEl ? submitBtnTextEl.textContent : ''

    if (submitBtn) {
      submitBtn.disabled = true
    }

    try {
      const productDataPromises = []

      for (const [productHandle] of this.selectedSwatches) {
        productDataPromises.push(this.fetchProductData(productHandle))
      }

      const productDataResults = await Promise.all(productDataPromises)
      const cartItems = []

      for (let i = 0; i < productDataResults.length; i++) {
        const productData = productDataResults[i]
        if (productData && productData.selectedVariantId) {
          cartItems.push({
            id: productData.selectedVariantId,
            quantity: 1,
          })
        }
      }

      if (cartItems.length > 0) {
        await window.BAO.Cart.addItem(cartItems, {
          sections: window.BAO.sectionRenderer.pushCartSections.map(
            (s) => s.section,
          ),
        })
      }

      this.clearAllSwatches()

      if (submitBtnTextEl) {
        submitBtnTextEl.textContent =
          window.theme.strings.swatchAddedToCartSuccess
        setTimeout(() => {
          if (submitBtnTextEl) submitBtnTextEl.textContent = originalText
          if (submitBtn) submitBtn.disabled = false
        }, 2000)
      }
    } catch (error) {
      console.error('Error adding swatches to cart:', error)

      const submitBtn = this.els.submitBtn?.element
      const submitBtnTextEl = submitBtn?.querySelector(
        '.clc-FixedBar_SubmitBtn-text',
      )
      if (submitBtnTextEl) submitBtnTextEl.textContent = originalText
      if (submitBtn) submitBtn.disabled = false

      alert('Failed to add swatches to cart. Please try again.')
    }
  }

  clearAllSwatches () {
    this.selectedSwatches.clear()
    sessionStorage.removeItem('BAO-swatch-builder-selections')

    this.els.swatchItem.elements = this.querySelectorAll(
      this.els.swatchItem.selector,
    )

    this.els.swatchItem.elements.forEach((slot) => this.resetSlot(slot))

    this.setVisibility(false)

    if (this.els.submitBtnSubtotal?.element) {
      this.els.submitBtnSubtotal.element.textContent = this.formatPrice(0)
    }

    this.calculateAndUpdatePrice()
  }
}

if (!customElements.get('swatch-builder-fixed-bar')) {
  customElements.define('swatch-builder-fixed-bar', SwatchBuilderFixedBar)
}
